import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Search, Plus, Download, RefreshCw, ChevronRight, X,
  Mail, Phone, MapPin, Calendar,
  Check, AlertCircle, Trash2, Edit3, ExternalLink, Megaphone, Sparkles, Wand2,
} from 'lucide-react';
import api from '../../lib/api';

const STATUSES = [
  { v: 'waitlist',       label: 'En lista',          color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { v: 'deposit_paid',   label: 'Señal pagada',       color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { v: 'partially_paid', label: 'Pago parcial',       color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { v: 'confirmed',      label: 'Confirmada',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { v: 'cancelled',      label: 'Cancelada',          color: 'bg-gray-50 text-gray-500 border-gray-200' },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.v, s]));

const ROOM_LABEL = {
  share: 'Doble compartida',
  single: 'Individual',
  either: 'Indistinto',
};

const fmtEur = (n) => (Number(n) || 0).toFixed(2) + ' €';
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const StatusPill = ({ status }) => {
  const s = STATUS_MAP[status] || { label: status, color: 'bg-gray-50 text-gray-600 border-gray-200' };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.color}`}>
      {s.label}
    </span>
  );
};

const computePaid = (e) => {
  const payments = e.payments || [];
  const confirmed = payments.filter(p => p.status === 'confirmed').reduce((s, p) => s + (p.amount || 0), 0);
  const pending = payments.filter(p => p.status === 'pending_validation').reduce((s, p) => s + (p.amount || 0), 0);
  return { confirmed, pending };
};

const computePrice = (retreat, enrollment) => {
  if (!retreat || !enrollment) return 0;
  if (enrollment.price_override) return Number(enrollment.price_override);
  const base = enrollment.room_preference === 'single'
    ? retreat.pricing.single.amount
    : retreat.pricing.double.amount;
  const eb = retreat.pricing.early_bird_until;
  const now = new Date();
  const isLate = eb ? now > new Date(eb) : false;
  return isLate ? base + (retreat.pricing.late_surcharge || 0) : base;
};

export default function AdminRetreats() {
  const [retreats, setRetreats] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [retreat, setRetreat] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showManualForm, setShowManualForm] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showSpecialPricingModal, setShowSpecialPricingModal] = useState(false);

  // Load retreats list
  useEffect(() => {
    api.get('/admin/retreats')
      .then(r => {
        setRetreats(r.data || []);
        if (r.data && r.data.length > 0) setSelectedSlug(r.data[0].slug);
      })
      .catch(() => toast.error('No pude cargar los retiros'))
      .finally(() => setLoading(false));
  }, []);

  // Load enrollments when selectedSlug changes
  const loadEnrollments = async (slug) => {
    if (!slug) return;
    setRefreshing(true);
    try {
      const [rRetreat, rEnroll] = await Promise.all([
        api.get(`/admin/retreats/${slug}`),
        api.get(`/admin/retreats/${slug}/enrollments`),
      ]);
      setRetreat(rRetreat.data);
      setEnrollments(rEnroll.data || []);
    } catch {
      toast.error('Error al cargar inscripciones');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (selectedSlug) loadEnrollments(selectedSlug);
  }, [selectedSlug]);

  // Filtered list
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enrollments.filter(e => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (!q) return true;
      return (
        (e.user_name || '').toLowerCase().includes(q) ||
        (e.user_email || '').toLowerCase().includes(q) ||
        (e.personal_data?.full_name || '').toLowerCase().includes(q) ||
        (e.personal_data?.dni || '').toLowerCase().includes(q)
      );
    });
  }, [enrollments, search, filterStatus]);

  const selected = enrollments.find(e => e.id === selectedId);

  // Stats
  const stats = useMemo(() => {
    const totalConfirmed = enrollments.filter(e => ['confirmed', 'deposit_paid', 'partially_paid'].includes(e.status)).length;
    const totalWaitlist = enrollments.filter(e => e.status === 'waitlist').length;
    const totalCancelled = enrollments.filter(e => e.status === 'cancelled').length;
    let revenue = 0; let pendingValidation = 0;
    enrollments.forEach(e => {
      const { confirmed, pending } = computePaid(e);
      revenue += confirmed;
      pendingValidation += pending;
    });
    return { totalConfirmed, totalWaitlist, totalCancelled, revenue, pendingValidation };
  }, [enrollments]);

  const handleExportCsv = async () => {
    if (!selectedSlug) return;
    try {
      const token = localStorage.getItem('token');
      const url = `${window.location.origin}/api/admin/retreats/${selectedSlug}/enrollments/export`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Error CSV');
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `retiro-${selectedSlug}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success('CSV descargado');
    } catch {
      toast.error('No pude generar el CSV');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#D4A84B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!retreats.length) {
    return (
      <div className="p-8 text-center text-slate-600">
        Aún no hay retiros configurados.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="admin-retreats-panel">
      {/* Header */}
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider text-[#8a7428] mb-1">Retiros presenciales</p>
        <h1 className="font-heading text-2xl sm:text-3xl text-slate-800">Super-panel de retiros</h1>
        <p className="text-sm text-slate-500 mt-1">Inscripciones, pagos, dietas y habitaciones en una sola pantalla.</p>
      </div>

      {/* Retreat selector tabs */}
      {retreats.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {retreats.map(r => (
            <button
              key={r.slug}
              onClick={() => { setSelectedSlug(r.slug); setSelectedId(null); }}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                selectedSlug === r.slug
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`retreat-tab-${r.slug}`}
            >
              {r.title}
            </button>
          ))}
        </div>
      )}

      {/* Stats strip */}
      {retreat && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="retreat-stats">
          <StatCard label="Inscritas" value={enrollments.length} sub={`/ ${retreat.capacity} plazas`} />
          <StatCard label="Confirmadas/Reservadas" value={stats.totalConfirmed} color="text-emerald-600" />
          <StatCard label="En lista" value={stats.totalWaitlist} color="text-amber-600" />
          <StatCard label="Recaudado" value={fmtEur(stats.revenue)} color="text-[#8a7428]" />
          <StatCard
            label="Pdte. validar"
            value={fmtEur(stats.pendingValidation)}
            color={stats.pendingValidation > 0 ? 'text-orange-600' : 'text-slate-400'}
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-white rounded-2xl border border-slate-200 p-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, email o DNI…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-2 py-1.5 text-sm bg-transparent focus:outline-none"
            data-testid="search-enrollments"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
          data-testid="filter-status"
        >
          <option value="all">Todos los estados</option>
          {STATUSES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <button
          onClick={() => loadEnrollments(selectedSlug)}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refrescar
        </button>
        <button
          onClick={handleExportCsv}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          data-testid="export-csv-btn"
        >
          <Download className="w-3.5 h-3.5" /> CSV monasterio
        </button>
        <button
          onClick={() => setShowBroadcastModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
          data-testid="broadcasts-btn"
        >
          <Megaphone className="w-3.5 h-3.5" /> Mensaje al grupo
        </button>
        <button
          onClick={() => setShowSpecialPricingModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#D4A84B] hover:bg-[#b89220] text-[#1a140a] rounded-lg"
          data-testid="special-pricing-btn"
        >
          <Sparkles className="w-3.5 h-3.5" /> Precio alumnado actual
        </button>
        <button
          onClick={() => setShowManualForm(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#2d3748] text-white rounded-lg hover:bg-black"
          data-testid="manual-enroll-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Añadir inscripción
        </button>
      </div>

      {/* Master-detail */}
      <div className="grid lg:grid-cols-[minmax(320px,420px)_1fr] gap-4">
        {/* List */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 flex justify-between">
            <span>Inscritas</span>
            <span>{filtered.length}</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="enrollments-list">
            {filtered.length === 0 ? (
              <div className="p-6 text-sm text-slate-400 text-center">Sin resultados</div>
            ) : filtered.map(e => {
              const { confirmed, pending } = computePaid(e);
              const isSel = e.id === selectedId;
              return (
                <button
                  key={e.id}
                  onClick={() => setSelectedId(e.id)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 transition-colors ${
                    isSel ? 'bg-[#FDFBF5]' : 'hover:bg-slate-50'
                  }`}
                  data-testid={`enrollment-${e.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-slate-800 truncate">
                        {e.personal_data?.full_name || e.user_name || e.user_email}
                        {e.manual && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-slate-400">(manual)</span>}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{e.user_email}</p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <StatusPill status={e.status} />
                        <span className="text-[10px] text-slate-400">
                          {ROOM_LABEL[e.room_preference] || '—'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-emerald-700">{fmtEur(confirmed)}</p>
                      {pending > 0 && (
                        <p className="text-[10px] text-orange-600">+{fmtEur(pending)} pdte.</p>
                      )}
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 mt-1 flex-shrink-0 ${isSel ? 'text-[#D4A84B]' : 'text-slate-300'}`} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div>
          {selected ? (
            <DetailPanel
              retreat={retreat}
              enrollment={selected}
              onUpdated={() => loadEnrollments(selectedSlug)}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-400 min-h-[300px] flex flex-col items-center justify-center">
              <ChevronRight className="w-8 h-8 text-slate-200 mb-3" />
              Selecciona una inscrita para ver el detalle.
            </div>
          )}
        </div>
      </div>

      {/* Manual enroll modal */}
      {showManualForm && (
        <ManualEnrollModal
          slug={selectedSlug}
          onClose={() => setShowManualForm(false)}
          onCreated={() => { setShowManualForm(false); loadEnrollments(selectedSlug); }}
        />
      )}

      {/* Broadcasts modal */}
      {showBroadcastModal && (
        <BroadcastsModal
          slug={selectedSlug}
          onClose={() => setShowBroadcastModal(false)}
        />
      )}

      {/* Special pricing modal */}
      {showSpecialPricingModal && (
        <SpecialPricingModal
          slug={selectedSlug}
          onClose={() => setShowSpecialPricingModal(false)}
        />
      )}
    </div>
  );
}

const StatCard = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4">
    <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</p>
    <p className={`font-heading text-xl ${color || 'text-slate-800'}`}>{value}</p>
    {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

/* ============== DETAIL PANEL ============== */
const DetailPanel = ({ retreat, enrollment, onUpdated, onClose }) => {
  const slug = retreat.slug;
  const { confirmed, pending } = computePaid(enrollment);
  const finalPrice = computePrice(retreat, enrollment);
  const remaining = Math.max(0, finalPrice - confirmed);

  const [tab, setTab] = useState('overview');

  const updateField = async (field, value) => {
    try {
      await api.put(`/admin/retreats/${slug}/enrollments/${enrollment.id}`, { [field]: value });
      toast.success('Guardado');
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/admin/retreats/${slug}/enrollments/${enrollment.id}/status`, { status: newStatus });
      toast.success('Estado actualizado');
      onUpdated();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const validatePayment = async (paymentId, action) => {
    try {
      await api.put(`/admin/retreats/${slug}/enrollments/${enrollment.id}/payments/${paymentId}/validate`, { action });
      toast.success(action === 'confirm' ? 'Pago confirmado' : 'Pago rechazado');
      onUpdated();
    } catch {
      toast.error('Error al validar');
    }
  };

  const deleteEnrollment = async () => {
    if (!window.confirm(`¿Eliminar definitivamente la inscripción de ${enrollment.user_name || enrollment.user_email}?\nUsa "Cancelar" si solo quieres marcarla como cancelada.`)) return;
    try {
      await api.delete(`/admin/retreats/${slug}/enrollments/${enrollment.id}`);
      toast.success('Inscripción eliminada');
      onUpdated();
      onClose();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="detail-panel">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-heading text-lg text-slate-800 truncate">
            {enrollment.personal_data?.full_name || enrollment.user_name || enrollment.user_email}
          </h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <a href={`mailto:${enrollment.user_email}`} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
              <Mail className="w-3 h-3" /> {enrollment.user_email}
            </a>
            {enrollment.personal_data?.phone && (
              <a href={`tel:${enrollment.personal_data.phone}`} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                <Phone className="w-3 h-3" /> {enrollment.personal_data.phone}
              </a>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full" data-testid="close-detail-btn">
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Status + actions */}
      <div className="px-5 py-3 bg-slate-50/50 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <select
          value={enrollment.status}
          onChange={(e) => updateStatus(e.target.value)}
          className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-[#D4A84B]"
          data-testid="status-select"
        >
          {STATUSES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <div className="flex-1" />
        <button
          onClick={deleteEnrollment}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg"
          data-testid="delete-enrollment-btn"
        >
          <Trash2 className="w-3 h-3" /> Eliminar
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 bg-white sticky top-0">
        {[
          { id: 'overview',  l: 'Resumen' },
          { id: 'payments',  l: `Pagos${pending > 0 ? ' •' : ''}` },
          { id: 'personal',  l: 'Datos' },
          { id: 'diet',      l: 'Dieta' },
          { id: 'room',      l: 'Habitación' },
          { id: 'intention', l: 'Intención' },
          { id: 'notes',     l: 'Notas internas' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-xs border-b-2 transition-colors ${
              tab === t.id ? 'border-[#D4A84B] text-[#8a7428] font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`detail-tab-${t.id}`}
          >{t.l}</button>
        ))}
      </div>

      {/* Tab body */}
      <div className="p-5 max-h-[60vh] overflow-y-auto">
        {tab === 'overview' && (
          <OverviewTab
            retreat={retreat}
            enrollment={enrollment}
            confirmed={confirmed}
            pending={pending}
            finalPrice={finalPrice}
            remaining={remaining}
            onUpdate={updateField}
          />
        )}
        {tab === 'payments'  && <PaymentsTab enrollment={enrollment} onValidate={validatePayment} />}
        {tab === 'personal'  && <PersonalDataTab enrollment={enrollment} />}
        {tab === 'diet'      && <DietTab enrollment={enrollment} />}
        {tab === 'room'      && <RoomTab enrollment={enrollment} onUpdate={updateField} onReload={onUpdated} slug={slug} />}
        {tab === 'intention' && <IntentionTab enrollment={enrollment} />}
        {tab === 'notes'     && <NotesTab enrollment={enrollment} onUpdate={updateField} />}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-50 last:border-0">
    <span className="text-xs uppercase tracking-wider text-slate-400 flex-shrink-0">{label}</span>
    <span className="text-sm text-slate-700 text-right break-words">{value || <em className="text-slate-300">—</em>}</span>
  </div>
);

const OverviewTab = ({ retreat, enrollment, confirmed, pending, finalPrice, remaining, onUpdate }) => {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [override, setOverride] = useState(enrollment.price_override || '');
  const [overrideReason, setOverrideReason] = useState(enrollment.admin_notes || '');

  const saveOverride = async () => {
    const num = override === '' ? null : parseFloat(override);
    if (override !== '' && (isNaN(num) || num < 0)) {
      toast.error('Precio inválido');
      return;
    }
    await onUpdate('price_override', num);
    if (overrideReason && overrideReason !== enrollment.admin_notes) {
      await onUpdate('admin_notes', overrideReason);
    }
    setOverrideOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Money summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#8a7428] mb-1">Precio total</p>
          <p className="font-heading text-lg text-[#8a7428]">{fmtEur(finalPrice)}</p>
          {enrollment.price_override != null && (
            <p className="text-[10px] text-slate-500 mt-0.5 italic">precio ajustado</p>
          )}
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-emerald-700 mb-1">Pagado</p>
          <p className="font-heading text-lg text-emerald-700">{fmtEur(confirmed)}</p>
          {pending > 0 && <p className="text-[10px] text-orange-600 mt-0.5">+{fmtEur(pending)} pdte.</p>}
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pendiente</p>
          <p className="font-heading text-lg text-slate-700">{fmtEur(remaining)}</p>
        </div>
      </div>

      {/* Price override */}
      <div>
        <button
          onClick={() => setOverrideOpen(!overrideOpen)}
          className="text-xs text-[#8a7428] hover:underline inline-flex items-center gap-1"
          data-testid="toggle-price-override"
        >
          <Edit3 className="w-3 h-3" />
          {enrollment.price_override != null ? 'Editar precio especial / beca' : 'Aplicar precio especial / beca'}
        </button>
        {overrideOpen && (
          <div className="mt-2 bg-slate-50 rounded-xl p-3 space-y-2">
            <label className="block">
              <span className="text-xs text-slate-500 block mb-1">Precio final personalizado (€)</span>
              <input
                type="number"
                step="0.01"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                placeholder="Deja vacío para usar el precio normal"
                className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                data-testid="price-override-input"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500 block mb-1">Motivo (visible solo aquí)</span>
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Ej: beca completa, descuento amiga, etc."
                className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                data-testid="price-override-reason"
              />
            </label>
            <button
              onClick={saveOverride}
              className="px-3 py-1.5 text-xs bg-[#2d3748] text-white rounded-lg hover:bg-black"
              data-testid="save-price-override"
            >Guardar</button>
          </div>
        )}
      </div>

      {/* Quick info */}
      <div>
        <Row label="Habitación" value={ROOM_LABEL[enrollment.room_preference]} />
        <Row label="Compañera asignada" value={enrollment.room_match} />
        <Row label="Inscrita el" value={fmtDate(enrollment.signed_up_at)} />
        <Row label="DNI" value={enrollment.personal_data?.dni} />
        <Row label="Teléfono" value={enrollment.personal_data?.phone} />
        <Row label="Dieta" value={enrollment.diet?.diet_type} />
        <Row label="Alergias" value={enrollment.diet?.allergies} />
      </div>
    </div>
  );
};

const PaymentsTab = ({ enrollment, onValidate }) => {
  const payments = enrollment.payments || [];
  if (payments.length === 0) return <p className="text-sm text-slate-400 italic text-center py-8">Sin pagos registrados.</p>;
  return (
    <div className="space-y-2">
      {[...payments].reverse().map(p => {
        const stat = {
          confirmed: { l: 'Confirmado',     c: 'bg-emerald-50 text-emerald-700' },
          pending_validation: { l: 'Pendiente validar', c: 'bg-amber-50 text-amber-700' },
          rejected: { l: 'Rechazado', c: 'bg-red-50 text-red-700' },
        }[p.status] || { l: p.status, c: 'bg-gray-50 text-gray-600' };
        const methodLabel = { paypal: 'PayPal', transfer: 'Transferencia', bizum: 'Bizum' }[p.method] || p.method;
        return (
          <div key={p.id} className="border border-slate-200 rounded-xl p-3" data-testid={`payment-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-heading text-lg text-slate-800">{fmtEur(p.amount)}</p>
                <p className="text-xs text-slate-500">{methodLabel} · {fmtDate(p.created_at)}</p>
                {p.note && <p className="text-xs text-slate-500 italic mt-1">"{p.note}"</p>}
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${stat.c}`}>{stat.l}</span>
            </div>
            {p.receipt_url && (
              <div className="mt-2">
                <a
                  href={p.receipt_url}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#8a7428] hover:underline"
                  data-testid={`receipt-link-${p.id}`}
                >
                  <ExternalLink className="w-3 h-3" /> Ver comprobante
                </a>
              </div>
            )}
            {p.status === 'pending_validation' && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => onValidate(p.id, 'confirm')}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                  data-testid={`confirm-payment-${p.id}`}
                >
                  <Check className="w-3 h-3" /> Confirmar pago
                </button>
                <button
                  onClick={() => onValidate(p.id, 'reject')}
                  className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50 rounded-lg"
                  data-testid={`reject-payment-${p.id}`}
                >
                  Rechazar
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PersonalDataTab = ({ enrollment }) => {
  const pd = enrollment.personal_data || {};
  return (
    <div>
      <Row label="Nombre completo" value={pd.full_name} />
      <Row label="DNI / Pasaporte" value={pd.dni} />
      <Row label="Fecha nacimiento" value={pd.date_of_birth} />
      <Row label="Nacionalidad" value={pd.nationality} />
      <Row label="Teléfono" value={pd.phone} />
      <Row label="Dirección" value={pd.address_line} />
      <Row label="Ciudad" value={pd.city} />
      <Row label="CP" value={pd.postal_code} />
      <Row label="Contacto emergencia" value={pd.emergency_contact_name} />
      <Row label="Tel. emergencia" value={pd.emergency_contact_phone} />
      <Row label="Consentimiento imagen" value={pd.image_consent ? 'Sí' : 'No'} />
      {pd.notes && <Row label="Notas" value={pd.notes} />}
    </div>
  );
};

const DietTab = ({ enrollment }) => {
  const di = enrollment.diet || {};
  if (!di.diet_type && !di.allergies && !di.intolerances && !di.medication && !di.extra_notes) {
    return <p className="text-sm text-slate-400 italic text-center py-8">La alumna aún no ha rellenado sus preferencias alimentarias.</p>;
  }
  return (
    <div>
      <Row label="Tipo de dieta" value={di.diet_type} />
      <Row label="Alergias" value={di.allergies} />
      <Row label="Intolerancias" value={di.intolerances} />
      <Row label="Medicación" value={di.medication} />
      <Row label="Notas extra" value={di.extra_notes} />
    </div>
  );
};

const RoomTab = ({ enrollment, onUpdate, onReload, slug }) => {
  const [match, setMatch] = useState(enrollment.room_match || '');
  const [singleSaving, setSingleSaving] = useState(false);

  const toggleSingle = async (approved) => {
    setSingleSaving(true);
    try {
      const r = await api.put(`/admin/retreats/${slug}/enrollments/${enrollment.id}/single-room`, { approved });
      if (approved && r.data?.new_price > (r.data?.paid || 0)) {
        const pending = (r.data.new_price - r.data.paid).toFixed(2);
        toast.success(`Individual aprobada. Quedan ${pending}€ pendientes de pagar.`);
      } else {
        toast.success(approved ? 'Individual aprobada' : 'Individual revocada');
      }
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSingleSaving(false);
    }
  };

  const wantsSingle = enrollment.room_preference === 'single';
  const singleApproved = !!enrollment.single_room_approved;

  return (
    <div className="space-y-4">
      <Row label="Preferencia" value={ROOM_LABEL[enrollment.room_preference]} />
      {enrollment.room_match_request && (
        <div className="bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#8a7428] mb-1">Pide compartir con</p>
          <p className="text-sm text-slate-800 font-medium">{enrollment.room_match_request}</p>
          {enrollment.room_match_note && (
            <p className="text-xs text-slate-500 italic mt-1">"{enrollment.room_match_note}"</p>
          )}
        </div>
      )}

      {/* Single room approval — only when alumna has requested 'single' */}
      {wantsSingle && (
        <div className={`rounded-xl p-3 border ${
          singleApproved ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
        }`} data-testid="single-room-block">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className={`text-[10px] uppercase tracking-wider mb-1 ${singleApproved ? 'text-emerald-700' : 'text-amber-700'}`}>
                Habitación individual
              </p>
              <p className="text-sm font-medium text-slate-800">
                {singleApproved ? '✅ Aprobada' : '⏳ Solicitada · pendiente de aprobación'}
              </p>
              {singleApproved && enrollment.single_room_approved_at && (
                <p className="text-[10px] text-slate-500 mt-1">Aprobada el {new Date(enrollment.single_room_approved_at).toLocaleDateString('es-ES')}</p>
              )}
            </div>
            {singleApproved ? (
              <button
                onClick={() => toggleSingle(false)}
                disabled={singleSaving}
                className="text-xs px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-white disabled:opacity-50"
                data-testid="revoke-single"
              >
                Revocar
              </button>
            ) : (
              <button
                onClick={() => toggleSingle(true)}
                disabled={singleSaving}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                data-testid="approve-single"
              >
                <Check className="w-3 h-3" /> Aprobar individual
              </button>
            )}
          </div>
        </div>
      )}

      <div>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Compañera asignada</span>
          <input
            type="text"
            value={match}
            onChange={(e) => setMatch(e.target.value)}
            placeholder="Nombre o email de la compañera"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
            data-testid="room-match-input"
          />
        </label>
        <button
          onClick={() => onUpdate('room_match', match || null)}
          className="mt-2 px-3 py-1.5 text-xs bg-[#2d3748] text-white rounded-lg hover:bg-black"
          data-testid="save-room-match"
        >Guardar</button>
      </div>
    </div>
  );
};

const IntentionTab = ({ enrollment }) => (
  <div className="space-y-4">
    {enrollment.intention_text && (
      <div className="bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-xl p-4">
        <p className="text-[10px] uppercase tracking-wider text-[#8a7428] mb-2">Carta de intención (al inscribirse)</p>
        <p className="text-sm text-slate-700 italic leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>"{enrollment.intention_text}"</p>
      </div>
    )}
    {enrollment.starlight_intention && (
      <div className="bg-gradient-to-br from-[#1a1f2e] to-[#2d3748] rounded-xl p-4 text-white">
        <p className="text-[10px] uppercase tracking-wider text-[#D4A84B] mb-2">Bajo las estrellas</p>
        <p className="text-sm text-white/90 italic leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>"{enrollment.starlight_intention}"</p>
      </div>
    )}
    {!enrollment.intention_text && !enrollment.starlight_intention && (
      <p className="text-sm text-slate-400 italic text-center py-8">Sin intenciones registradas todavía.</p>
    )}
  </div>
);

const NotesTab = ({ enrollment, onUpdate }) => {
  const [notes, setNotes] = useState(enrollment.admin_notes || '');
  return (
    <div>
      <textarea
        rows={6}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Anota lo que necesites recordar sobre esta persona. Solo tú lo ves."
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-[#D4A84B] resize-none"
        data-testid="admin-notes-input"
      />
      <button
        onClick={() => onUpdate('admin_notes', notes)}
        className="mt-2 px-3 py-1.5 text-xs bg-[#2d3748] text-white rounded-lg hover:bg-black"
        data-testid="save-admin-notes"
      >Guardar</button>
    </div>
  );
};

/* ============== MANUAL ENROLL MODAL ============== */
const ManualEnrollModal = ({ slug, onClose, onCreated }) => {
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    room_preference: 'either',
    status: 'waitlist',
    intention_text: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.email.trim() || !form.email.includes('@')) {
      toast.error('Email obligatorio');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/retreats/${slug}/enrollments/manual`, form);
      toast.success('Inscripción creada');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="manual-enroll-modal"
      >
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="font-heading text-lg text-slate-800">Añadir inscripción manual</h3>
            <p className="text-xs text-slate-500 mt-1">Si la persona no existe, se crea su cuenta sin contraseña.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-slate-500 block mb-1">Email *</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
              data-testid="manual-email"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 block mb-1">Nombre completo</span>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
              data-testid="manual-name"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-500 block mb-1">Habitación</span>
              <select
                value={form.room_preference}
                onChange={(e) => setForm({ ...form, room_preference: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                data-testid="manual-room"
              >
                <option value="either">Indistinto</option>
                <option value="share">Doble compartida</option>
                <option value="single">Individual</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500 block mb-1">Estado inicial</span>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                data-testid="manual-status"
              >
                <option value="waitlist">En lista</option>
                <option value="deposit_paid">Señal pagada</option>
                <option value="confirmed">Confirmada</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-500 block mb-1">Carta de intención (opcional)</span>
            <textarea
              rows={3}
              value={form.intention_text}
              onChange={(e) => setForm({ ...form, intention_text: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B] resize-none"
              data-testid="manual-intention"
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
          >Cancelar</button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#2d3748] text-white rounded-lg hover:bg-black disabled:opacity-60"
            data-testid="manual-submit"
          >{saving ? 'Creando…' : 'Crear inscripción'}</button>
        </div>
      </div>
    </div>
  );
};

/* ============== BROADCASTS MODAL ============== */
const BroadcastsModal = ({ slug, onClose }) => {
  const [messages, setMessages] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/admin/retreats/${slug}/broadcasts`);
      setMessages(r.data || []);
    } catch { setMessages([]); }
  };
  useEffect(() => { load(); }, [slug]);

  const submit = async () => {
    if (!body.trim()) { toast.error('Escribe el mensaje'); return; }
    setSaving(true);
    try {
      await api.post(`/admin/retreats/${slug}/broadcasts`, { title: title.trim(), body: body.trim() });
      toast.success('Mensaje enviado al grupo');
      setTitle(''); setBody('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Borrar este mensaje?')) return;
    try {
      await api.delete(`/admin/retreats/${slug}/broadcasts/${id}`);
      toast.success('Borrado');
      await load();
    } catch { toast.error('Error'); }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="broadcasts-modal">
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="font-heading text-lg text-slate-800">Mensaje al grupo del retiro</h3>
            <p className="text-xs text-slate-500 mt-1">Lo verán todas las personas inscritas en su sección "Comunicaciones".</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Composer */}
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-4 space-y-3 mb-6">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título (opcional)"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400 bg-white"
            data-testid="broadcast-title"
          />
          <textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Escribe aquí el mensaje para todo el grupo…"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400 bg-white resize-none"
            data-testid="broadcast-body"
          />
          <button
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-60"
            data-testid="broadcast-send"
          >
            <Megaphone className="w-4 h-4" />
            {saving ? 'Enviando…' : 'Enviar al grupo'}
          </button>
        </div>

        {/* Past messages */}
        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-3">Historial</p>
        {messages === null && <p className="text-sm text-slate-400 italic">Cargando…</p>}
        {messages && messages.length === 0 && <p className="text-sm text-slate-400 italic">Aún no has enviado ningún mensaje.</p>}
        {messages && messages.length > 0 && (
          <div className="space-y-2" data-testid="broadcasts-history">
            {messages.map(m => (
              <div key={m.id} className="bg-white border border-slate-100 rounded-xl p-3" data-testid={`broadcast-row-${m.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    {m.title && <p className="text-sm font-medium text-slate-800">{m.title}</p>}
                    <p className="text-[10px] text-slate-400">{fmt(m.created_at)}</p>
                    <p className="text-sm text-slate-700 leading-relaxed mt-1 whitespace-pre-wrap">{m.body}</p>
                  </div>
                  <button onClick={() => remove(m.id)} className="text-red-500 hover:bg-red-50 p-1 rounded-full" data-testid={`broadcast-delete-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


/* ============== SPECIAL PRICING MODAL — Precio alumnado actual ============== */
const SpecialPricingModal = ({ slug, onClose }) => {
  const [config, setConfig] = useState(null);
  const [bulkText, setBulkText] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [adding, setAdding] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [pickedSuggestions, setPickedSuggestions] = useState({}); // email -> bool

  const reload = async () => {
    try {
      const r = await api.get(`/admin/retreats/${slug}/special-pricing`);
      setConfig(r.data || {});
    } catch { toast.error('No pude cargar la configuración'); }
  };
  useEffect(() => { reload(); }, [slug]);

  const fmtDateForInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      // YYYY-MM-DD for <input type="date">
      return d.toISOString().split('T')[0];
    } catch { return ''; }
  };

  const updateConfigField = (field, value) => setConfig({ ...config, [field]: value });

  const saveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    try {
      const payload = {
        enabled: !!config.enabled,
        price_double: Number(config.price_double) || 0,
        deadline: config.deadline,
        title: (config.title || '').trim(),
        message: (config.message || '').trim(),
      };
      await api.put(`/admin/retreats/${slug}/special-pricing/config`, payload);
      toast.success('Configuración guardada');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingConfig(false);
    }
  };

  const addBulk = async () => {
    const emails = bulkText
      .split(/[\s,;]+/)
      .map(s => s.trim().toLowerCase())
      .filter(s => s && s.includes('@'));
    if (!emails.length) { toast.error('Pega al menos un email válido'); return; }
    setAdding(true);
    try {
      const r = await api.post(`/admin/retreats/${slug}/special-pricing/emails`, {
        emails,
        note: bulkNote.trim() || null,
      });
      toast.success(`Añadidos ${r.data?.added || 0} (${r.data?.total || 0} en total)`);
      setBulkText(''); setBulkNote('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setAdding(false);
    }
  };

  const removeEmail = async (email) => {
    if (!window.confirm(`¿Quitar ${email} de la lista?`)) return;
    try {
      await api.delete(`/admin/retreats/${slug}/special-pricing/emails/${encodeURIComponent(email)}`);
      toast.success('Quitado');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const loadSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const r = await api.get(`/admin/retreats/${slug}/special-pricing/suggest`);
      setSuggestions(r.data || []);
      // Pre-mark not-yet-added students as candidates
      const initial = {};
      (r.data || []).forEach(s => { if (!s.already_in_list) initial[s.email] = true; });
      setPickedSuggestions(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al sugerir');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const addPickedSuggestions = async () => {
    const emails = Object.entries(pickedSuggestions).filter(([, v]) => v).map(([k]) => k);
    if (!emails.length) { toast.error('No has marcado a nadie'); return; }
    setAdding(true);
    try {
      const r = await api.post(`/admin/retreats/${slug}/special-pricing/emails`, {
        emails,
        note: 'Alumnado activo (sugerencia automática)',
      });
      toast.success(`Añadidos ${r.data?.added || 0}`);
      setSuggestions(null);
      setPickedSuggestions({});
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setAdding(false);
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  if (!config) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl px-8 py-6 text-sm text-slate-500" onClick={(e) => e.stopPropagation()}>Cargando…</div>
      </div>
    );
  }

  const emails = config.emails || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="special-pricing-modal">
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4A84B] mb-1">Tarifa especial</p>
            <h3 className="font-heading text-xl text-slate-800 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D4A84B]" /> Precio alumnado actual
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-lg">
              Solo lo verán las personas cuyo email esté en la lista. Aplica únicamente a habitación doble.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full" data-testid="special-pricing-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Config */}
          <section className="space-y-3 bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-[#8a7428]">Configuración</p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!config.enabled}
                  onChange={(e) => updateConfigField('enabled', e.target.checked)}
                  data-testid="sp-enabled-toggle"
                />
                <span>{config.enabled ? 'Activa' : 'Desactivada'}</span>
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-500 block mb-1">Precio especial (habitación doble)</span>
                <input
                  type="number"
                  step="0.01"
                  value={config.price_double ?? ''}
                  onChange={(e) => updateConfigField('price_double', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                  data-testid="sp-price"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-500 block mb-1">Caduca el</span>
                <input
                  type="date"
                  value={fmtDateForInput(config.deadline)}
                  onChange={(e) => updateConfigField('deadline', e.target.value ? new Date(`${e.target.value}T23:59:59Z`).toISOString() : '')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                  data-testid="sp-deadline"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-xs text-slate-500 block mb-1">Título visible</span>
              <input
                type="text"
                value={config.title || ''}
                onChange={(e) => updateConfigField('title', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                data-testid="sp-title"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500 block mb-1">Mensaje (opcional)</span>
              <textarea
                rows={2}
                value={config.message || ''}
                onChange={(e) => updateConfigField('message', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B] resize-none"
                data-testid="sp-message"
              />
            </label>
            <div className="flex justify-end">
              <button
                onClick={saveConfig}
                disabled={savingConfig}
                className="px-4 py-2 text-sm bg-[#2d3748] text-white rounded-lg hover:bg-black disabled:opacity-60"
                data-testid="sp-save-config"
              >
                {savingConfig ? 'Guardando…' : 'Guardar configuración'}
              </button>
            </div>
          </section>

          {/* Bulk add */}
          <section className="space-y-3 border border-slate-200 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-wider text-slate-500">Añadir emails manualmente</p>
            <textarea
              rows={3}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="alumna1@ejemplo.com, alumna2@ejemplo.com&#10;alumno3@ejemplo.com"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B] resize-none"
              data-testid="sp-bulk-emails"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                placeholder="Nota (opcional) — p.ej. 'Facilitadoras 3ª ed.'"
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#D4A84B]"
                data-testid="sp-bulk-note"
              />
              <button
                onClick={addBulk}
                disabled={adding}
                className="px-4 py-2 text-sm bg-[#D4A84B] hover:bg-[#b89220] text-[#1a140a] rounded-lg disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                data-testid="sp-bulk-add"
              >
                <Plus className="w-3.5 h-3.5" />
                {adding ? 'Añadiendo…' : 'Añadir a la lista'}
              </button>
            </div>
          </section>

          {/* Suggestions */}
          <section className="border border-slate-200 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs uppercase tracking-wider text-slate-500">Sugerir alumnado activo</p>
              <button
                onClick={loadSuggestions}
                disabled={loadingSuggestions}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-[#D4A84B]/40 text-[#8a7428] rounded-lg hover:bg-[#FDFBF5] disabled:opacity-60"
                data-testid="sp-suggest-btn"
              >
                <Wand2 className="w-3.5 h-3.5" />
                {loadingSuggestions ? 'Buscando…' : (suggestions ? 'Volver a buscar' : 'Sugerir desde formaciones en venta abierta')}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Lista a personas con matrícula activa en cualquier formación que esté ahora mismo en estado <strong>“venta abierta”</strong>.
            </p>
            {suggestions !== null && suggestions.length === 0 && (
              <p className="text-sm text-slate-400 italic">Ninguna persona encontrada.</p>
            )}
            {suggestions !== null && suggestions.length > 0 && (
              <>
                <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100" data-testid="sp-suggestions-list">
                  {suggestions.map(s => (
                    <label key={s.email} className={`flex items-center gap-3 px-3 py-2 ${s.already_in_list ? 'bg-slate-50' : 'hover:bg-[#FDFBF5]'}`}>
                      <input
                        type="checkbox"
                        disabled={s.already_in_list}
                        checked={!!pickedSuggestions[s.email] || !!s.already_in_list}
                        onChange={(e) => setPickedSuggestions({ ...pickedSuggestions, [s.email]: e.target.checked })}
                        data-testid={`sp-suggest-pick-${s.email}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 truncate">{s.name || s.email}</p>
                        <p className="text-xs text-slate-500 truncate">{s.email}</p>
                      </div>
                      <span className="text-[10px] text-slate-400 truncate max-w-[40%] text-right">
                        {s.already_in_list ? 'Ya en la lista' : (s.source || '')}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={addPickedSuggestions}
                    disabled={adding}
                    className="px-4 py-2 text-sm bg-[#2d3748] text-white rounded-lg hover:bg-black disabled:opacity-60"
                    data-testid="sp-suggest-confirm"
                  >
                    Añadir seleccionadas
                  </button>
                </div>
              </>
            )}
          </section>

          {/* Current list */}
          <section className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Lista actual ({emails.length})
            </p>
            {emails.length === 0 ? (
              <p className="text-sm text-slate-400 italic">La lista está vacía.</p>
            ) : (
              <div className="border border-slate-100 rounded-xl divide-y divide-slate-100" data-testid="sp-emails-list">
                {emails.slice().sort((a, b) => (a.email || '').localeCompare(b.email || '')).map(e => (
                  <div key={e.email} className="flex items-center gap-3 px-3 py-2" data-testid={`sp-email-row-${e.email}`}>
                    <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{e.email}</p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {e.note ? `${e.note} · ` : ''}Añadido {fmt(e.added_at)} {e.added_by ? `por ${e.added_by}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => removeEmail(e.email)}
                      className="text-red-500 hover:bg-red-50 p-1 rounded-full"
                      data-testid={`sp-email-delete-${e.email}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
